/**
 * 3-card KPI section (Quality / Delivery / Financial) for Cached Performance
 * tab (CR-4751). Mirrors KPIMetricsSection visual language from DefaultTab.
 */
import type {
  DashboardSnapshot, MetricType, TrendPoint,
} from '../../../lib/api/dashboard-snapshots'
import { CachedKPICell } from './CachedKPICell'

interface CachedKPISectionProps {
  latestByMetric: Map<MetricType, DashboardSnapshot>
  trends: Record<MetricType, TrendPoint[]>
}

export function CachedKPISection({ latestByMetric, trends }: CachedKPISectionProps) {
  return (
    <div className="bg-card rounded-lg shadow-sm p-4">
      <div className="grid grid-cols-3 gap-4 items-stretch">
        {/* Quality */}
        <div className="border border-red-200 bg-red-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-red-600 mb-4 text-sm uppercase tracking-wide">Quality</h3>
          <div className="grid grid-cols-2 gap-3 items-start flex-1">
            <CachedKPICell
              label="PPM"
              snapshot={latestByMetric.get('ppm')}
              trend={trends.ppm}
              lowerIsBetter
              emphasis="destructive"
            />
            <CachedKPICell
              label="NCRs"
              snapshot={latestByMetric.get('ncr_count')}
              trend={trends.ncr_count}
              lowerIsBetter
            />
          </div>
        </div>

        {/* Delivery */}
        <div className="border border-green-200 bg-green-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-green-600 mb-4 text-sm uppercase tracking-wide">Delivery</h3>
          <div className="grid grid-cols-3 gap-3 items-start flex-1">
            <CachedKPICell
              label="OTIF"
              snapshot={latestByMetric.get('otif_percentage')}
              trend={trends.otif_percentage}
            />
            <CachedKPICell
              label="OTD"
              snapshot={latestByMetric.get('otd_percentage')}
              trend={trends.otd_percentage}
            />
            <CachedKPICell
              label="Appeals"
              snapshot={latestByMetric.get('appeals_count')}
              trend={trends.appeals_count}
              lowerIsBetter
            />
          </div>
        </div>

        {/* Financial */}
        <div className="border border-blue-200 bg-blue-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-blue-600 mb-4 text-sm uppercase tracking-wide">Financial</h3>
          <div className="grid grid-cols-3 gap-3 items-start flex-1">
            <CachedKPICell
              label="Open Orders"
              snapshot={latestByMetric.get('open_order_value')}
              trend={trends.open_order_value}
            />
            <CachedKPICell
              label="Total Spend"
              snapshot={latestByMetric.get('total_spend')}
              trend={trends.total_spend}
            />
            <CachedKPICell
              label="Arrears"
              snapshot={latestByMetric.get('arrears_value')}
              trend={trends.arrears_value}
              lowerIsBetter
            />
          </div>
        </div>
      </div>
    </div>
  )
}
